<template>
<div>
  <!-- Hawaii does a special calculation of deviation from average -->
<div v-if="postgres.stusps === 'HI'">
  <p class="mt-1"><a href="https://elections.hawaii.gov/wp-content/uploads/2015/03/2011ReapportionmentPlan_ExecutiveSummaryandReport_2011-12-29.pdf" target="_blank">Hawaii's state constitution</a> requires that equal population for state legislative districts be determined differently than in other states.
    </p>
    <p>In particular, Hawaii's reapportionment commission is required to allocate the total number of members of each house of the state legislature among the four basic island units (Hawaii, Maui, Kauai and Oahu) using the total number of permanent residents of each basic island unit. Then, the commission apportions the number of legislators allocated to each basic island unit among the districts so that for each house the average number of permanent residents per member in each district is as nearly equal to the average for the basic island unit as practicable. 
  </p>
  <p>Therefore, the question of how much each district's population is above or below the average district size will not be known until the commission makes these determinations.
  </p>
</div>

  <!-- Illinois and Utah deviation post-2010 was zero -->
<div v-else-if="postgres.stusps === 'IL' || postgres.stusps === 'UT'">
  <p class="mt-1">When {{ postgres.statename }} mapped its current state legislative districts after the 2010 Census, the population of <a href="https://www.ncsl.org/research/redistricting/2010-ncsl-redistricting-deviation-table.aspx" target="_blank">each state senate district was equal</a>.</p>

      <span v-if="postgres.popdifffromavg > 0">
      <p>
        Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
        <strong>{{ postgres.districtname }}'s</strong> population is
        <strong>above</strong> the state average. In order to draw equal population
        districts, the district would need to
        <strong
          ><em>shrink</em> by as many as
          {{ Math.round(postgres.popdifffromavg).toLocaleString() }}
          people</strong
        >.<!--  (<em>This is an estimate.</em> The actual change will depend on the
        2020 Census results, available later in 2021.) -->
      </p>
    </span>

    <span v-else-if="postgres.popdifffromavg < 0">
      <p>
        Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
        <strong>{{ postgres.districtname }}'s</strong> population is
        <strong>below</strong> the state average. In order to draw equal population
        districts, the district would need to
        <strong
          ><em>expand</em> by as many as
          {{ Math.abs(Math.round(postgres.popdifffromavg)).toLocaleString() }}
          people</strong
        >.<!--  (<em>This is an estimate.</em> The actual change will depend on the
        2020 Census results, available later in 2021.) -->
      </p>
    </span>

    <span v-else>
      <p>
        Based on current population estimates, this district's population is close enough to the statewide
        average that it may not need to expand or shrink.
      </p>
    </span>
</div>

  <!-- Deviation for all other states was more than zero -->
<div v-else>
          
<p v-if="postgres.statefips === '37'" class="mt-1">When {{ postgres.statename }} mapped its state legislative districts after the 2010 Census, each one was within <a href="https://www.ncsl.org/research/redistricting/2010-ncsl-redistricting-deviation-table.aspx" target="_blank">an overall range of {{ postgres.statedeviationpost2010 }}%</a> (i.e., as much as {{ (postgres.statedeviationpost2010 / 2).toFixed(1) }}% smaller or larger than the average district population). The calculations below reflect the current 2019 districts.</p>

<p v-else class="mt-1">When {{ postgres.statename }} mapped its current state legislative districts after the 2010 Census, each one was within <a href="https://www.ncsl.org/research/redistricting/2010-ncsl-redistricting-deviation-table.aspx" target="_blank">an overall range of {{ postgres.statedeviationpost2010 }}%</a> (i.e., as much as {{ (postgres.statedeviationpost2010 / 2).toFixed(1) }}% smaller or larger than the average district population).</p>
      <!-- 
Above avg / needs to shrink
-- within prior deviation
-- outside prior deviation

Below avg / needs to expand
-- within prior deviation
-- outside prior deviation
-->
      <span v-if="postgres.popdifffromavg > 0">
        <p
          v-if="
            Math.abs(postgres.deviation * 100).toFixed(1) >
            postgres.statedeviationpost2010 / 2
          "
        >
          Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
          <strong>{{ postgres.districtname }}'s</strong> deviation is
          <strong>outside this range</strong>. In order to draw equal
          population districts, the district may need to
          <strong
            ><em>shrink</em> by as many as
            {{
              Math.round(postgres.popdifffromavg).toLocaleString()
            }}
            people</strong
          >.<!--  (<em>This is an estimate.</em> The actual change will depend on the
          2020 Census results, available later in 2021.) -->
        </p>
        <p
          v-if="
            Math.abs(postgres.deviation * 100).toFixed(1) <=
            postgres.statedeviationpost2010 / 2
          "
        >
          Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
          <strong>{{ postgres.districtname }}'s</strong> deviation is within
          this range. <strong>It may not have to expand or shrink during redistricting.</strong> Other factors, however, may require its boundaries to change.<!--  (<em>This is an estimate.</em> The actual change will depend on the 2020 Census results, available later in 2021.)  -->
        </p>
      </span>

      <span v-else-if="postgres.popdifffromavg < 0">
        <p
          v-if="
            Math.abs(postgres.deviation * 100).toFixed(1) >
            postgres.statedeviationpost2010 / 2
          "
        >
          Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
          <strong>{{ postgres.districtname }}'s</strong> deviation is
          <strong>outside this range</strong>. In order to draw equal
          population districts, the district may need to
          <strong
            ><em>expand</em> by as many as
            {{
              Math.abs(Math.round(postgres.popdifffromavg)).toLocaleString()
            }}
            people</strong
          >.<!--  (<em>This is an estimate.</em> The actual change will depend on the
          2020 Census results, available later in 2021.) -->
        </p>
        <p
          v-if="
            Math.abs(postgres.deviation * 100).toFixed(1) <=
            postgres.statedeviationpost2010 / 2
          "
        >
          Based on the district's 2020 population compared with {{ postgres.statename }}'s 2020 population,
          <strong>{{ postgres.districtname }}'s</strong> deviation is within
          this range. <strong>It may not have to expand or shrink during redistricting.</strong> Other factors, however, may require its boundaries to change.<!--  (<em>This is an estimate.</em> The actual change will depend on the 2020 Census results, available later in 2021.) --> 
        </p>
      </span>

      <span v-else>
        <p>
          When {{ postgres.statename }} mapped new districts after the 2010
          Census, they were equal in size.
        </p>
        <p>
          Based on current population estimates, {{ postgres.districtname }}'s population is close enough to the statewide average that it may not need to expand or shrink.
        </p>
      </span>
  </div>

    <span>
      Learn more about redistricting in {{ postgres.statename }}:
    </span>
    <ul class="mb-2">
      <li>{{ postgres.stusps }}'s <a v-bind:href="'https://redistricting.lls.edu/state/' + postgres.statename.replace(' ','-') + '/?cycle=2020&level=State%20Upper'" target="_blank">process for redrawing state legislative districts</a> <span class="text-caption">(via <a href="https://redistricting.lls.edu/" target="_blank">All About Redistricting</a>)</span>;
      </li>
      <li>how the <a v-bind:href="'https://gerrymander.princeton.edu/reforms/' + postgres.stusps" target="_blank">latest Census-related redistricting timeline delays</a> might impact {{ postgres.stusps }} <span class="text-caption">(via the <a href="https://gerrymander.princeton.edu/" target="_blank">Princeton Gerrymandering Project</a>)</span>; and
      </li>
      <li>
        <a v-bind:href="'https://planscore.org/' + postgres.statename.toLowerCase().replace(' ','_') + '/#!2018-plan-statesenate-eg'" target="_blank">metrics to evaluate {{ postgres.stusps }}'s current districts</a> <span class="text-caption">(via <a href="https://planscore.org/" target="_blank">PlanScore</a>)</span>.
      </li>
    </ul>

<!-- POPULATION CHANGE SECTION (moved from separate Vue template for now) -->
    <v-app-bar color="#c0d7e4" dense height="30px" flat>
      <span class="text-uppercase" style="color: #1d3557; font-weight: 500"
        >Population change
      </span>
    </v-app-bar>
    <p class="mt-1">
      <!-- <span v-if="postgres.popchg1020 > 0"> -->
      <span v-if="postgres.popchgpct1020 > 0.001">
        Between 2010 and 2020,
        <span class="text-popincrease-color font-weight-bold">{{ postgres.districtname }}'s population grew by
          {{ ((postgres.popchgpct1020) * 100).toFixed(1) }}% (an
          increase of
          {{ Math.round(postgres.popchg1020).toLocaleString() }} people from
          {{ Math.round(postgres.pop2010).toLocaleString() }})</span>.
      </span>
      <!-- <span v-else-if="postgres.popchg1020 < 0"> -->
      <span v-else-if="postgres.popchgpct1020 < -0.001">
        Between 2010 and 2020,
        <span class="text-popdecrease-color font-weight-bold">{{ postgres.districtname }}'s population fell by
          {{ Math.abs((postgres.popchgpct1020) * 100).toFixed(1) }}% (a decrease of
          {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }}
          people from {{ Math.round(postgres.pop2010).toLocaleString() }})</span>.
      </span>
      <!-- <span v-else>
        Between 2010 and 2020, {{ postgres.districtname }}'s population neither increased nor decreased.
      </span> -->
      <span v-else-if="postgres.popchgpct1020 >= -0.001 && postgres.popchgpct1020 < 0">
        Between 2010 and 2020, <strong>{{ postgres.districtname }}'s population effectively did not change</strong> (only decreasing less than 0.1%, or {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }} from {{ Math.round(postgres.pop2010).toLocaleString() }} people).
      </span>
      <span v-else-if="postgres.popchgpct1020 > 0 && postgres.popchgpct1020 < 0.001">
        Between 2010 and 2020, <strong>{{ postgres.districtname }}'s population effectively did not change</strong> (only increasing less than 0.1%, or {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }} from {{ Math.round(postgres.pop2010).toLocaleString() }} people).
      </span>
      <span v-else>
        Between 2010 and 2020, {{ postgres.districtname }}'s population neither increased nor decreased.
      </span>
    </p>
    <p class="text-caption">
      Population change is calculated by comparing the district's <a href="https://www2.census.gov/programs-surveys/decennial/2020/data/01-Redistricting_File--PL_94-171/" target="_blank">2020 population</a> with <a href="https://www2.census.gov/census_2010/redistricting_file--pl_94-171/" target="_blank">2010 population
      counts</a> allocated from census blocks to current district
      boundaries.
    </p>

</div>
</template>
<script>
import { Enums } from "../../../../data/Config/Config";
export default {
  name: "OnePOneVStUpper",
  components: {},
  data: function () {
    return {};
  },
  mounted: function () {},
  methods: {},
  watch: {},
  computed: {
    postgres: function () {
      return (
        this.$store.getters.getSelectedData[Enums.DATA_SOURCE.POSTGRES] || {}
      );
    },
  },
};
</script>
<style></style>